/**
 * Generic accessible combobox (typeahead + browsable dropdown) used to
 * select a value from a merchant-managed list before adding a product to
 * the cart. Despite the "breed-picker" name (its original use case), this
 * widget is shared by any `data-breed-picker` block on the page - e.g. the
 * dog breed picker on "genetic-traits" and the disease/trait picker on
 * "copy-of-single-diseases".
 *
 * - The valid list is embedded per-widget as JSON (sourced from a shop
 *   metafield in Liquid). Each entry is either a plain string (display
 *   value doubles as the stored value, e.g. dog breeds), or an object
 *   `{ name, slug }` where `name` is shown to the customer everywhere on
 *   screen and `slug` is a unique machine key stored as a *hidden*
 *   line-item property (see `data-breed-slug-property-name`) so the
 *   merchant can unambiguously tell apart entries that share a display
 *   name (e.g. two diseases with the same Hebrew name).
 * - A selection is only considered valid once the typed text matches one
 *   of the listed entries' names exactly, ignoring leading/trailing
 *   whitespace, repeated internal spaces, letter case, and Hebrew
 *   final-letter (sofit) forms.
 * - Add-to-cart forms are blocked (and an inline error shown) until a
 *   valid entry is selected; the selection is injected as a line-item
 *   property into whichever add-to-cart form is actually submitted
 *   (there can be more than one on the page, e.g. the sticky bar).
 */
(function () {
  var FINAL_LETTERS = { '\u05DA': '\u05DB', '\u05DD': '\u05DE', '\u05DF': '\u05E0', '\u05E3': '\u05E4', '\u05E5': '\u05E6' };

  function normalize(value) {
    return (value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[\u05DA\u05DD\u05DF\u05E3\u05E5]/g, function (ch) {
        return FINAL_LETTERS[ch];
      });
  }

  function itemName(item) {
    return typeof item === 'string' ? item : (item && item.name) || '';
  }

  function itemSlug(item) {
    return typeof item === 'string' ? null : (item && item.slug) || null;
  }

  function createWidget(root) {
    var dataScript = root.parentElement.querySelector('[data-breed-picker-data]');
    var items = [];
    try {
      items = JSON.parse(dataScript.textContent || '[]');
    } catch (err) {
      items = [];
    }
    if (!items.length) return null;

    var sortedItems = items.slice().sort(function (a, b) {
      return itemName(a).localeCompare(itemName(b), 'he');
    });
    var byNormalized = Object.create(null);
    items.forEach(function (item) {
      byNormalized[normalize(itemName(item))] = item;
    });

    var input = root.querySelector('[data-breed-picker-input]');
    var toggle = root.querySelector('[data-breed-picker-toggle]');
    var listbox = root.querySelector('[data-breed-picker-listbox]');
    var errorEl = root.querySelector('[data-breed-picker-error]');
    var propertyName = root.getAttribute('data-breed-property-name') || 'Breed';
    var slugPropertyName = root.getAttribute('data-breed-slug-property-name') || '';
    var emptyText = root.getAttribute('data-breed-picker-empty-text') || 'לא נמצאו התאמות';
    var fixedVariantId = root.getAttribute('data-breed-fixed-variant-id') || '';

    var state = { valid: false, value: '', slug: null };
    var activeIndex = -1;
    var currentOptions = [];

    function renderOptions(query) {
      var normalizedQuery = normalize(query);
      var list;
      if (!normalizedQuery) {
        list = sortedItems;
      } else {
        var startsWith = [];
        var contains = [];
        sortedItems.forEach(function (item) {
          var normalizedName = normalize(itemName(item));
          if (normalizedName.indexOf(normalizedQuery) === 0) {
            startsWith.push(item);
          } else if (normalizedName.indexOf(normalizedQuery) > -1) {
            contains.push(item);
          }
        });
        list = startsWith.concat(contains);
      }

      currentOptions = list;
      activeIndex = -1;
      listbox.innerHTML = '';

      if (!list.length) {
        var empty = document.createElement('li');
        empty.className = 'breed-picker__empty';
        empty.textContent = emptyText;
        listbox.appendChild(empty);
        return;
      }

      list.forEach(function (item, index) {
        var option = document.createElement('li');
        option.className = 'breed-picker__option';
        option.setAttribute('role', 'option');
        option.id = listbox.id + '-option-' + index;
        option.textContent = itemName(item);
        option.addEventListener('mousedown', function (event) {
          // mousedown (not click) so it fires before the input's blur handler.
          event.preventDefault();
          selectItem(item);
          closeListbox();
        });
        listbox.appendChild(option);
      });
    }

    function openListbox() {
      renderOptions(input.value);
      listbox.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function closeListbox() {
      listbox.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      setActiveDescendant(-1);
    }

    function setActiveDescendant(index) {
      var options = listbox.querySelectorAll('.breed-picker__option');
      options.forEach(function (option) {
        option.removeAttribute('aria-selected');
      });
      activeIndex = index;
      if (index > -1 && options[index]) {
        options[index].setAttribute('aria-selected', 'true');
        options[index].scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', options[index].id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function selectItem(item) {
      input.value = itemName(item);
      state.valid = true;
      state.value = itemName(item);
      state.slug = itemSlug(item);
      input.setAttribute('aria-invalid', 'false');
      errorEl.hidden = true;
    }

    function invalidate() {
      state.valid = false;
      state.value = '';
      state.slug = null;
    }

    function showError() {
      errorEl.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    }

    input.addEventListener('input', function () {
      var normalizedValue = normalize(input.value);
      if (normalizedValue && byNormalized[normalizedValue]) {
        selectItem(byNormalized[normalizedValue]);
      } else {
        invalidate();
        if (errorEl.hidden === false && !normalizedValue) {
          errorEl.hidden = true;
          input.removeAttribute('aria-invalid');
        }
      }
      openListbox();
    });

    input.addEventListener('focus', openListbox);

    input.addEventListener('blur', function () {
      // Delay so a mousedown-selection on an option can complete first.
      window.setTimeout(function () {
        closeListbox();
        if (input.value.trim() && !state.valid) {
          showError();
        }
      }, 120);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (listbox.hidden) {
          openListbox();
        } else {
          setActiveDescendant(Math.min(activeIndex + 1, currentOptions.length - 1));
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!listbox.hidden) {
          setActiveDescendant(Math.max(activeIndex - 1, 0));
        }
      } else if (event.key === 'Enter') {
        if (!listbox.hidden && activeIndex > -1 && currentOptions[activeIndex]) {
          event.preventDefault();
          selectItem(currentOptions[activeIndex]);
          closeListbox();
        }
      } else if (event.key === 'Escape') {
        closeListbox();
      }
    });

    toggle.addEventListener('click', function () {
      if (listbox.hidden) {
        input.focus();
        openListbox();
      } else {
        closeListbox();
      }
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) {
        closeListbox();
      }
    });

    return {
      root: root,
      input: input,
      errorEl: errorEl,
      getState: function () {
        return state;
      },
      showError: showError,
      propertyName: propertyName,
      slugPropertyName: slugPropertyName,
      fixedVariantId: fixedVariantId
    };
  }

  function setFormVariantId(form, variantId) {
    var field = form.querySelector('[name="id"]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'id';
      form.appendChild(field);
    }
    field.value = variantId;
  }

  function setLineItemProperty(form, name, value) {
    var existing = form.querySelector('input[data-breed-picker-property="' + name + '"]');
    if (existing) {
      existing.value = value;
      return;
    }
    var hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.setAttribute('data-breed-picker-property', name);
    hiddenInput.name = 'properties[' + name + ']';
    hiddenInput.value = value;
    form.appendChild(hiddenInput);
  }

  function removeLineItemProperty(form, name) {
    var existing = form.querySelector('input[data-breed-picker-property="' + name + '"]');
    if (existing) existing.parentNode.removeChild(existing);
  }

  function init() {
    var widgets = Array.prototype.map
      .call(document.querySelectorAll('[data-breed-picker]'), createWidget)
      .filter(Boolean);

    if (!widgets.length) return;

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;

        var invalidWidget = widgets.find(function (widget) {
          return !widget.getState().valid;
        });

        if (invalidWidget) {
          event.preventDefault();
          event.stopImmediatePropagation();
          invalidWidget.showError();
          invalidWidget.input.focus();
          invalidWidget.input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        widgets.forEach(function (widget) {
          var state = widget.getState();
          setLineItemProperty(form, widget.propertyName, state.value);
          if (widget.slugPropertyName) {
            if (state.slug) {
              setLineItemProperty(form, widget.slugPropertyName, state.slug);
            } else {
              removeLineItemProperty(form, widget.slugPropertyName);
            }
          }
          if (widget.fixedVariantId) {
            // This widget's list only carries line-item properties, not real
            // variant selection - always add the same placeholder variant
            // regardless of whatever the form's native variant field says.
            setFormVariantId(form, widget.fixedVariantId);
          }
        });
      },
      true
    );
  }

  init();
})();
